import React, { useState } from 'react';
import { Modal, Radio, Button, RadioChangeEvent, Space, message } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router';
import { useQueryClient } from 'react-query';
import {
  Quotation,
  QuotationType,
  ReportQuotationItemsSuppliers,
  ReportQuotationType,
} from '@/core/domain/stock/Quotations/Quotations.types';
import {
  useItensFromQuotation,
  useQuotaionItemsBySuppliers,
  useWinnerQuotation,
} from '@/core/domain/stock/Quotations/Quotations.hooks';
import generateCsv from '@/utils/functions/generateCsv';
import { quantityFormatter } from '@/utils/number';

type Props = { visible: boolean; handleVisible: () => void };

const QuotationReportModal: React.FC<Props> = ({ visible, handleVisible }) => {
  const intl = useIntl();
  const { quotationId } = useRouteMatch<{ quotationId: string }>().params;

  const quotation = useQueryClient().getQueryData<Quotation | undefined>([
    'quotations',
    Number(quotationId),
  ]);

  const [filterType, setFilterType] = useState<QuotationType>(
    QuotationType.ItemsSuppliers,
  );
  const [orderType, setOrderType] = useState<
    'items' | 'lessPrice' | 'suppliers'
  >();
  const { mutateAsync: itemsBySuppliersMutation } =
    useQuotaionItemsBySuppliers();
  const { mutateAsync: itensFromQuotationMutation } = useItensFromQuotation();
  const { mutateAsync: winnerQuotationMutation } = useWinnerQuotation();

  const options = [
    {
      label: intl.formatMessage({
        id: 'quotations.details.report.itemsSuppliers',
      }),
      value: QuotationType.ItemsSuppliers,
    },
    {
      label: intl.formatMessage({
        id: 'quotations.details.report.suppliersItems',
      }),
      value: QuotationType.SupplierItems,
    },
    {
      label: intl.formatMessage({
        id: 'quotations.details.report.quotationItems',
      }),
      value: QuotationType.QuotationItems,
    },
  ];

  if (quotation && quotation.winner) {
    options.push({
      label: intl.formatMessage({
        id: 'quotations.details.report.supplierWinners',
      }),
      value: QuotationType.SupplierWinners,
    });
  }

  const supplierOrders = [
    {
      label: 'Itens',
      value: 'items',
    },
    {
      label: 'Menor preço',
      value: 'lessPrice',
    },
  ];

  const itemOrders = [
    {
      label: 'Fornecedores',
      value: 'suppliers',
    },
    {
      label: 'Menor preço',
      value: 'lessPrice',
    },
  ];

  const handleFilterType = ({ target: { value } }: RadioChangeEvent) => {
    setFilterType(value);
  };

  const handleOrderType = ({ target: { value } }: RadioChangeEvent) => {
    setOrderType(value);
  };

  const handleCancel = () => {
    setFilterType(QuotationType.ItemsSuppliers);
    setOrderType(undefined);
    handleVisible();
  };

  const showReport = () => {
    if (filterType === QuotationType.QuotationItems) {
      return window.open(
        `/privateReports/itemsByQuotation?quotationId=${quotationId}`,
        '_blank',
      );
    }

    if (filterType === QuotationType.SupplierWinners) {
      return window.open(
        `/privateReports/supplierWinners?quotationId=${quotationId}`,
        '_blank',
      );
    }

    if (!orderType) {
      return message.warning('Selecione um tipo de ordenação');
    }

    return window.open(
      `/privateReports/itemsSuppliers?quotationId=${quotationId}&type=${filterType}&order=${orderType}`,
    );
  };

  const itemsSuppliersDataCSV = (data: ReportQuotationItemsSuppliers) => {
    return data.quotation
      .map(({ name, items }) =>
        items.map(
          ({
            code,
            description,
            requestedQuantity,
            suppliedQuantity,
            unitPrice,
          }) => ({
            Peça:
              filterType === QuotationType.ItemsSuppliers
                ? name.replace('Peça: ', '').replace('Insumos: ', '')
                : description,
            Código: code,
            Fornecedor:
              filterType === QuotationType.ItemsSuppliers
                ? description
                : name.replace('Fornecedor: ', ''),
            Quantidade: requestedQuantity,
            'Qtde. fornecida': suppliedQuantity,
            'Valor unitário (R$)': quantityFormatter(unitPrice),
            'Valor total(R$)': quantityFormatter(suppliedQuantity * unitPrice),
          }),
        ),
      )
      .flat()
      .sort((a, b) =>
        orderType === 'lessPrice'
          ? a['Valor total(R$)'] > b['Valor total(R$)']
            ? 1
            : -1
          : filterType === QuotationType.ItemsSuppliers
          ? a.Fornecedor > b.Fornecedor
            ? 1
            : -1
          : a.Peça > b.Peça
          ? 1
          : -1,
      );
  };

  const handleCsv = async () => {
    switch (filterType) {
      case QuotationType.ItemsSuppliers:
        const responseItems = await itemsBySuppliersMutation({
          quotationId: Number(quotationId),
          type: ReportQuotationType.ItemsSupplier,
        });
        const responseItemsCSV = itemsSuppliersDataCSV(responseItems?.result);
        return generateCsv(responseItemsCSV, 'Itens por fornecedores');

      case QuotationType.SupplierItems:
        const responseSupplier = await itemsBySuppliersMutation({
          quotationId: Number(quotationId),
          type: ReportQuotationType.SupplierItems,
        });
        const responseSupplierCSV = itemsSuppliersDataCSV(
          responseSupplier?.result,
        );
        return generateCsv(responseSupplierCSV, 'Fornecedores por itens');

      case QuotationType.QuotationItems:
        const itensFromQuotation = await itensFromQuotationMutation(
          Number(quotationId),
        );
        const csvData = itensFromQuotation?.result?.items.map(
          ({ code, description, requestedQuantity }) => ({
            Código: code,
            Descrição: description,
            Quantidade: requestedQuantity,
            'Qtde. Fornecida': '',
            'Valor unitário(R$)': '',
            'Valor total(R$)': '',
          }),
        );
        return generateCsv(csvData, 'Itens da cotação');

      case QuotationType.SupplierWinners:
        return await winnerQuotationMutation(Number(quotationId));

      default:
        undefined;
    }
  };

  const footer = [
    <Button
      onClick={async () => await handleCsv()}
      disabled={filterType === QuotationType.SupplierWinners}
    >
      Gerar CSV
    </Button>,
    <Button key={0} onClick={showReport}>
      <FormattedMessage id="commons.report.viewPDF" />
    </Button>,
  ];

  return (
    <Modal
      title={intl.formatMessage({ id: 'quotations.details.report' })}
      open={visible}
      footer={footer}
      onCancel={handleCancel}
      width={550}
    >
      <Space size="large" direction="vertical">
        <Space direction="vertical">
          <strong>Filtrar por:</strong>

          <Radio.Group
            buttonStyle="solid"
            onChange={handleFilterType}
            value={filterType}
            optionType="button"
            options={options}
          />
        </Space>

        {!!filterType &&
          filterType !== QuotationType.QuotationItems &&
          filterType !== QuotationType.SupplierWinners && (
            <>
              <Space direction="vertical">
                <strong>Ordernar por:</strong>

                {filterType === QuotationType.ItemsSuppliers && (
                  <Radio.Group
                    buttonStyle="solid"
                    onChange={handleOrderType}
                    value={orderType}
                    optionType="button"
                    options={itemOrders}
                  />
                )}

                {filterType === QuotationType.SupplierItems && (
                  <Radio.Group
                    buttonStyle="solid"
                    onChange={handleOrderType}
                    value={orderType}
                    optionType="button"
                    options={supplierOrders}
                  />
                )}
              </Space>
            </>
          )}
      </Space>
    </Modal>
  );
};

export default QuotationReportModal;
